import { useState, useEffect, useRef } from "react";
import {
  Form,
  Table,
  Popconfirm,
  Button,
  Typography,
  Input,
  Space,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import HighLighter from "react-highlight-words";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  deleteDoc,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  getAuth,
  deleteUser,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { AddUser } from "./AddUser";
import { EditUser } from "./EditUser";
import { logUserAction } from "../../logs";
import { decrypt, APP_VERSION } from "../../utils";

export const TableUsers = () => {
  // select form
  const [form] = Form.useForm();
  // store data
  const [data, setData] = useState();
  // modal
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);

  // edit data
  const [edit, setEdit] = useState();
  // pagination
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  // search text
  const [search, setSearch] = useState("");
  // search field
  const [searchedCol, setSearchedCol] = useState("");
  // database
  const db = getFirestore();
  // firebase auth
  const auth = getAuth();
  // search input
  const searchInput = useRef(null);
  // map Users
  const mapUsers = (res) => {
    return res.docs.map((ele) => ({
      key: ele.id,
      ...ele.data(),
      status: !ele.data().status ? "inactivo" : "activo",
    }));
  };
  // get users
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getUsers = async () => {
    await getDocs(collection(db, "user")).then((res) => {
      setData(mapUsers(res));
      setTableParams({
        ...tableParams,
        total: mapUsers(res).length,
      });
    });
  };

  useEffect(() => {
    if (!data) {
      getUsers();
    }
  }, [data, getUsers]);

  const handleChange = (pagination) => {
    setTableParams({
      pagination,
    });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearch(selectedKeys[0]);
    setSearchedCol(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearch("");
  };
  const [sizeWindow, setSizeWindow] = useState(true)
  useEffect(() => {
    window.innerWidth > 1000 ? setSizeWindow(true) : setSizeWindow(false)
  }, [])


  const getColSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Buscar por ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
            className="btnOK"
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
            className="btnCancel"
          >
            Reiniciar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearch(selectedKeys[0]);
              setSearchedCol(dataIndex);
            }}
            className="text-link"
          >
            Filtrar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
            className="text-link"
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#189Off" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedCol === dataIndex ? (
        <HighLighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[search]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // fields
  const fields = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
      fixed: sizeWindow ? "left" : false,
      ...getColSearchProps("email"),
      sorter: (a, b) => a.email.localeCompare(b.email),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ...getColSearchProps("name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
      width: 150,
    },
    {
      title: "Apellido",
      dataIndex: "surname",
      key: "surname",
      width: 150,
      ...getColSearchProps("surname"),
      sorter: (a, b) => a.surname.localeCompare(b.surname),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Telefono",
      dataIndex: "phone",
      key: "phone",
      width: 150,
    },
    {
      title: "Contraseña",
      dataIndex: "password",
      key: "password",
      width: 150,
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
      fixed: sizeWindow ? "right" : false,
    },
    {
      title: "Operación",
      dataIndex: "operation",
      width: 100,
      fixed: sizeWindow ? "right" : false,
      render: (_, record) =>
        data.length >= 1 ? (
          <div style={{ textAlign: "center" }}>
            <Typography.Link onClick={() => selectEdit(record)} className="text-link">
              <EditOutlined />
            </Typography.Link>

            <Popconfirm
              title="¿Desea eliminar este usuario?"
              onConfirm={() => handleDelete(record)}
              okText="Si"
              cancelText="No"
              placement="topRight"
            >
              <Typography.Link style={{ marginLeft: "10px" }} className="text-link" >
                <DeleteOutlined />
              </Typography.Link>
            </Popconfirm>
          </div>
        ) : null,
    },
  ];

  const showAddUser = () => {
    setIsAddUserOpen(true);
  };

  const showEditUser = () => {
    setIsEditUserOpen(true);
  };

  const handleDelete = async (user) => {
    try {
      // app credentials
      const sessionEmail = await decrypt(localStorage.getItem("id"));
      const sessionPassword = await decrypt(localStorage.getItem("key"));
      const signInMethods = await fetchSignInMethodsForEmail(auth, user.email);
      // verify that user exist
      if (signInMethods.length > 0) {
        // log in as user to delete
        await signInWithEmailAndPassword(auth, user.email, user.password);
        // delete user
        await deleteUser(auth.currentUser);
        // log in back as current user
        await signInWithEmailAndPassword(auth, sessionEmail, sessionPassword);
        // update user collection
        await deleteDoc(doc(db, "user", user.key));
        // update users table
        await getUsers();
        // log user delete action
        logUserAction(db, auth, "DELETE_USER", user.key, null, null, APP_VERSION);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const selectEdit = async (userData) => {
    setEdit(userData);
    showEditUser();
  };

  return (
    <div>
      <Button
        onClick={showAddUser}
        type="primary"
        style={{
          marginBottom: 16,
        }}
        className="btnOK"
        size="large"
      >
        Crear usuario
      </Button>
      <Form form={form} component={false}>
        {!data || !data.length ? (
          <p>Cargando usuarios...</p>
        ) : (
          <Table
            bordered
            dataSource={data}
            columns={fields}
            pagination={tableParams.pagination}
            onChange={handleChange}
            scroll={{
              x: 1300,
              y: 490,
            }}
          />
        )}
      </Form>
      <AddUser
        isModalOpen={isAddUserOpen}
        setIsModalOpen={setIsAddUserOpen}
        updateData={getUsers}
      />
      <EditUser
        isModalOpen={isEditUserOpen}
        setIsModalOpen={setIsEditUserOpen}
        data={edit}
        updateData={getUsers}
      />
    </div>
  );
};
